import { create } from 'zustand';
import { api, renewToken, setMediaToken, setToken } from './api';
import { applyMode, applyTheme, savedMode } from './theme';
import { adoptPrefs, savePref } from './prefs';
import { useLang } from './i18n';
import type { MeResponse, ServerConfig, User } from './types';

export type TransportPref = 'auto' | 'webtransport' | 'websocket';

interface SessionState {
  config: ServerConfig | null;
  configError: string | null;
  me: MeResponse | null;
  user: User | null;
  ready: boolean;
  transportPref: TransportPref;
  load: () => Promise<void>;
  refreshMe: () => Promise<MeResponse | null>;
  signIn: (token: string, user: User) => Promise<void>;
  signOut: () => Promise<void>;
  setUser: (u: User) => void;
  setTransportPref: (p: TransportPref) => void;
}

function savedPref(): TransportPref {
  try {
    const v = localStorage.getItem('vb.transport');
    if (v === 'webtransport' || v === 'websocket' || v === 'auto') return v;
  } catch {
    /* ignore */
  }
  return 'auto';
}

function adopt(me: MeResponse) {
  setMediaToken(me.mediaToken);
  adoptPrefs(me.user.prefs);
  const tp = me.user.prefs?.transport;
  if (tp === 'auto' || tp === 'webtransport' || tp === 'websocket') useSession.setState({ transportPref: tp });
  applyTheme(me.user.theme);
  applyMode(savedMode()); // adoptPrefs above stored the profile's mode on this device
  if (me.user.lang === 'en' || me.user.lang === 'ja') useLang.getState().setLang(me.user.lang, false);
}

export const useSession = create<SessionState>((set, get) => ({
  config: null,
  configError: null,
  me: null,
  user: null,
  ready: false,
  transportPref: savedPref(),
  load: async () => {
    let config: ServerConfig | null = null;
    let configError: string | null = null;
    try {
      config = await api.config();
    } catch (e) {
      configError = (e as Error).message;
    }
    let me: MeResponse | null = null;
    if (config?.setupComplete && (await renewToken())) me = await api.me().catch(() => null);
    if (me) adopt(me);
    set({ config, configError, me, user: me?.user ?? null, ready: true });
  },
  refreshMe: async () => {
    const me = await api.me().catch(() => null);
    if (me) {
      adopt(me);
      set({ me, user: me.user });
    }
    return me;
  },
  signIn: async (token, user) => {
    setToken(token);
    set({ user });
    await get().refreshMe();
  },
  signOut: async () => {
    await api.logout().catch(() => undefined);
    setToken(null);
    set({ user: null, me: null });
  },
  setUser: (u) => {
    const me = get().me;
    set({ user: u, me: me ? { ...me, user: u } : me });
  },
  setTransportPref: (p) => {
    savePref('transport', p);
    set({ transportPref: p });
  },
}));

// Coming back to the app (another tab, the phone unlocked): pick up profile changes made on
// another device, e.g. a new picture, at most once a minute.
let lastFocusRefresh = 0;
document.addEventListener('visibilitychange', () => {
  if (document.visibilityState !== 'visible' || !useSession.getState().user || Date.now() - lastFocusRefresh < 60_000) return;
  lastFocusRefresh = Date.now();
  useSession.getState().refreshMe();
});

// Refresh the media token well before it expires (12 h).
setInterval(() => {
  if (useSession.getState().user) useSession.getState().refreshMe();
}, 60 * 60 * 1000);

/** Current member's permissions (host has all). */
export const usePerms = () => useSession((s) => s.me?.member?.perms);
export const useIsHost = () => useSession((s) => !!s.user?.host);

// Toasts ---------------------------------------------------------------------

export interface Toast {
  id: number;
  text: string;
  tone?: 'default' | 'accent' | 'red';
}

let toastId = 0;
export const useToasts = create<{ toasts: Toast[]; push: (text: string, tone?: Toast['tone']) => void; dismiss: (id: number) => void }>(
  (set, get) => ({
    toasts: [],
    push: (text, tone = 'default') => {
      const id = ++toastId;
      set({ toasts: [...get().toasts.slice(-3), { id, text, tone }] });
      setTimeout(() => get().dismiss(id), 4200);
    },
    dismiss: (id) => set({ toasts: get().toasts.filter((t) => t.id !== id) }),
  }),
);

export const toast = (text: string, tone?: Toast['tone']) => useToasts.getState().push(text, tone);
